import { PortalHost } from "@gorhom/portal";
import { type ReactNode, useId } from "react";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import CustomKeyboardAvoidingView from "../CustomKeyboardAvoidingView";
import { WritingSheetPortalContext } from "./WritingSheetPortalContext";
import { sessionMaxWidth } from "./constants";
import useNativeItemWidth from "./useNativeItemWidth";

type Props = {
	isSplitLayout?: boolean;
	sessionType?: "formative" | "summative";
	children: ReactNode;
};

function getAlignSelf(isMobile: boolean, isSummativeTest: boolean) {
	if (isMobile) {
		return undefined;
	}
	if (isSummativeTest) {
		return "flex-start" as const;
	}
	return "center" as const;
}

function getPaddingHorizontal(
	isSplitLayout: boolean,
	isMobile: boolean,
	isSummativeTest: boolean,
) {
	if (isMobile) {
		return 16;
	}
	if (isSplitLayout) {
		return 0;
	}

	if (isSummativeTest) {
		return 24;
	}
	return 0;
}

function getPaddingTop(
	isSplitLayout: boolean,
	isMobile: boolean,
	isSummativeTest: boolean,
) {
	if (isMobile) {
		return 16;
	}
	if (isSplitLayout) {
		return 0;
	}
	if (isSummativeTest) {
		return isMobile ? 16 : 24;
	}
	return 16;
}

const SessionItemShell = ({
	isSplitLayout,
	sessionType = "formative",
	children,
}: Props) => {
	const { isMobile } = useDevice();
	const isSummativeTest = sessionType === "summative";
	const webMaxWidth = isSplitLayout
		? sessionMaxWidth.splitLayout
		: sessionMaxWidth[sessionType];
	const nativeItemWidth = useNativeItemWidth(sessionType);
	const theme = useAppTheme();

	const writingSheetHostName = useId();

	const alignSelf = getAlignSelf(isMobile, isSummativeTest);

	const paddingHorizontal = getPaddingHorizontal(
		Boolean(isSplitLayout),
		isMobile,
		isSummativeTest,
	);
	const paddingTop = getPaddingTop(
		Boolean(isSplitLayout),
		isMobile,
		isSummativeTest,
	);

	if (isSplitLayout) {
		return (
			<Box flex={1} backgroundColor={theme.colors.background}>
				<Box
					flex={1}
					width={isMobile ? nativeItemWidth : "100%"}
					maxWidth={isMobile ? undefined : webMaxWidth}
					alignSelf={alignSelf}
					gap={16}
				>
					{children}
				</Box>
			</Box>
		);
	}

	return (
		<Box flex={1} backgroundColor={theme.colors.background}>
			<WritingSheetPortalContext value={writingSheetHostName}>
				<CustomKeyboardAvoidingView
					showsVerticalScrollIndicator={!isMobile}
					style={{ flex: 1 }}
					contentContainerStyle={{
						flexGrow: 1,
						gap: 16,
						alignSelf,
						width: isMobile ? nativeItemWidth : "100%",
						maxWidth: isMobile ? undefined : webMaxWidth,
						paddingBottom: 120,
						paddingHorizontal,
						paddingTop,
					}}
				>
					{children}
				</CustomKeyboardAvoidingView>
				<PortalHost name={writingSheetHostName} />
			</WritingSheetPortalContext>
		</Box>
	);
};

export default SessionItemShell;
